import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

export const RESIZE_DIRECTIONS: ResizeDirection[] = [
  'n',
  's',
  'e',
  'w',
  'ne',
  'nw',
  'se',
  'sw',
]

type Offset = { x: number; y: number }

type Size = { width: number; height: number }

type MoveBounds = { minX: number; maxX: number; minY: number; maxY: number }

type Gesture =
  | { type: 'move'; bounds: MoveBounds }
  | { type: 'resize'; direction: ResizeDirection }

type GestureOrigin = {
  pointer: Offset
  offset: Offset
  size: Size
  panelRect: DOMRect
  gesture: Gesture
}

// パネルを画面外へ運び出して掴み直せなくなるのを防ぐため、
// 上端は画面内に留め、左右はこのぶんだけ画面内に残す。
const MIN_VISIBLE_SIZE = 80

// 中身（会話履歴と入力欄）が破綻しない最小サイズ。
const MIN_WIDTH = 360
const MIN_HEIGHT = 320

// 既定サイズ。画面に収まらない場合は最大サイズまで縮める。
const DEFAULT_WIDTH = 760
const DEFAULT_HEIGHT = 1040

// パネルの外に残す画面の余白。上下左右で同じ幅にする
// （CSSが持つパネルの固定位置もこの値を使う）。
export const PANEL_SCREEN_MARGIN = 24

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const calcMaxSize = (): Size => ({
  width: Math.max(MIN_WIDTH, window.innerWidth - PANEL_SCREEN_MARGIN * 2),
  height: Math.max(MIN_HEIGHT, window.innerHeight - PANEL_SCREEN_MARGIN * 2),
})

const calcInitialSize = (): Size => {
  const maxSize = calcMaxSize()
  return {
    width: Math.min(DEFAULT_WIDTH, maxSize.width),
    height: Math.min(DEFAULT_HEIGHT, maxSize.height),
  }
}

// 移動を始めた時点のパネル位置から、動かせる範囲を求める。移動中に
// パネルの大きさとウィンドウの大きさは変わらないため、開始時に一度だけ計算する。
const calcMoveBounds = (
  panelRect: DOMRect,
  currentOffset: Offset
): MoveBounds => {
  // オフセットを除いた、CSSが決めている素の配置位置。
  const baseLeft = panelRect.left - currentOffset.x
  const baseTop = panelRect.top - currentOffset.y
  return {
    minX: MIN_VISIBLE_SIZE - panelRect.width - baseLeft,
    maxX: window.innerWidth - MIN_VISIBLE_SIZE - baseLeft,
    minY: -baseTop,
    maxY: window.innerHeight - MIN_VISIBLE_SIZE - baseTop,
  }
}

// 掴んだ辺だけを動かした結果のサイズ。反対側の辺は動かさない。
// 右辺・下辺は動かすと画面の外へ出ていけるため、画面端で止める
// （左辺・上辺は反対側が画面内に留まるので、最大サイズだけで足りる）。
const calcResizedSize = (
  origin: GestureOrigin,
  direction: ResizeDirection,
  delta: Offset
): Size => {
  const maxSize = calcMaxSize()
  const { width, height } = origin.size
  const { panelRect } = origin
  const deltaX = Math.min(delta.x, window.innerWidth - panelRect.right)
  const deltaY = Math.min(delta.y, window.innerHeight - panelRect.bottom)
  return {
    width: direction.includes('e')
      ? clamp(width + deltaX, MIN_WIDTH, maxSize.width)
      : direction.includes('w')
        ? clamp(width - delta.x, MIN_WIDTH, maxSize.width)
        : width,
    height: direction.includes('s')
      ? clamp(height + deltaY, MIN_HEIGHT, maxSize.height)
      : direction.includes('n')
        ? clamp(height - delta.y, MIN_HEIGHT, maxSize.height)
        : height,
  }
}

/**
 * パネルの位置と大きさを、ヘッダー掴みでの移動と縁掴みでのリサイズで変えるフック。
 *
 * パネルはCSSで画面の右下に固定されているため、右辺・下辺を掴んだリサイズでは
 * 広げたぶんだけパネルをずらし、掴んだ辺だけが動くようにする。
 *
 * 位置と大きさはコンポーネントが生きている間だけ保持する
 * （リロードすると既定の位置・大きさに戻る）。
 */
export const usePanelGeometry = (): {
  panelRef: RefObject<HTMLDivElement>
  panelStyle: CSSProperties
  handleMoveStart: (event: ReactMouseEvent<HTMLElement>) => void
  handleResizeStart: (
    direction: ResizeDirection
  ) => (event: ReactMouseEvent<HTMLElement>) => void
} => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 })
  const [size, setSize] = useState<Size>(calcInitialSize)
  const [isGesturing, setIsGesturing] = useState(false)
  const gestureOriginRef = useRef<GestureOrigin>()

  const startGesture = (
    event: ReactMouseEvent<HTMLElement>,
    createGesture: (panelRect: DOMRect) => Gesture
  ) => {
    const panel = panelRef.current
    if (!panel) return
    // 掴んだまま動かすと文字が選択され、操作中の表示が乱れる。
    event.preventDefault()
    const panelRect = panel.getBoundingClientRect()
    gestureOriginRef.current = {
      pointer: { x: event.clientX, y: event.clientY },
      offset,
      size,
      panelRect,
      gesture: createGesture(panelRect),
    }
    setIsGesturing(true)
  }

  const handleMoveStart = (event: ReactMouseEvent<HTMLElement>) => {
    // ハンドル上に並ぶボタン（リセット・閉じる）の操作を移動として拾わない。
    if (event.target instanceof Element && event.target.closest('button')) {
      return
    }
    startGesture(event, panelRect => ({
      type: 'move',
      bounds: calcMoveBounds(panelRect, offset),
    }))
  }

  const handleResizeStart =
    (direction: ResizeDirection) => (event: ReactMouseEvent<HTMLElement>) =>
      startGesture(event, () => ({ type: 'resize', direction }))

  useEffect(() => {
    if (!isGesturing) return
    const handleMouseMove = (event: MouseEvent) => {
      // ウィンドウの外でボタンを離すと mouseup を受け取れないため、
      // ボタンが押されていない移動を検知した時点で操作を終える。
      if (event.buttons === 0) {
        setIsGesturing(false)
        return
      }
      const origin = gestureOriginRef.current
      if (!origin) return
      const delta = {
        x: event.clientX - origin.pointer.x,
        y: event.clientY - origin.pointer.y,
      }
      if (origin.gesture.type === 'move') {
        const { bounds } = origin.gesture
        setOffset({
          x: clamp(origin.offset.x + delta.x, bounds.minX, bounds.maxX),
          y: clamp(origin.offset.y + delta.y, bounds.minY, bounds.maxY),
        })
        return
      }
      const { direction } = origin.gesture
      const resized = calcResizedSize(origin, direction, delta)
      setSize(resized)
      // 右辺・下辺は、広げたぶんだけパネルをずらさないと反対側の辺が動いてしまう。
      setOffset({
        x: direction.includes('e')
          ? origin.offset.x + resized.width - origin.size.width
          : origin.offset.x,
        y: direction.includes('s')
          ? origin.offset.y + resized.height - origin.size.height
          : origin.offset.y,
      })
    }
    const handleMouseUp = () => setIsGesturing(false)
    // パネルの外へポインタが出ても追従させるため、ウィンドウで受ける。
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isGesturing])

  // ウィンドウが狭くなったとき、パネルが画面を覆い尽くさないように縮める。
  useEffect(() => {
    const handleWindowResize = () => {
      const maxSize = calcMaxSize()
      setSize(current => ({
        width: Math.min(current.width, maxSize.width),
        height: Math.min(current.height, maxSize.height),
      }))
    }
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  return {
    panelRef,
    panelStyle: {
      transform: `translate(${offset.x}px, ${offset.y}px)`,
      width: size.width,
      height: size.height,
    },
    handleMoveStart,
    handleResizeStart,
  }
}
